/**
 * Sample Datasets Component
 * Provides quick access to demo datasets
 */

interface SampleDataset {
  name: string;
  description: string;
  filename: string;
  icon: string;
  rows: number;
}

const SAMPLE_DATASETS: SampleDataset[] = [
  {
    name: 'Retail Sales',
    description: 'Monthly retail sales data with seasonal patterns',
    filename: 'retail-sales.csv',
    icon: '🛍️',
    rows: 48,
  },
  {
    name: 'Web Traffic',
    description: 'Daily website visitors with weekly seasonality',
    filename: 'web-traffic.csv',
    icon: '📈',
    rows: 90,
  },
  {
    name: 'Stock Prices',
    description: 'Daily stock closing prices with trend',
    filename: 'stock-prices.csv',
    icon: '💹',
    rows: 60,
  },
];

interface SampleDatasetsProps {
  onLoadSample: (file: File) => void;
}

export function SampleDatasets({ onLoadSample }: SampleDatasetsProps) {
  const handleLoadSample = async (dataset: SampleDataset) => {
    try {
      const response = await fetch(`/samples/${dataset.filename}`);
      const blob = await response.blob();
      const file = new File([blob], dataset.filename, { type: 'text/csv' });
      onLoadSample(file);
    } catch (error) {
      console.error('Failed to load sample dataset:', error);
    }
  };

  return (
    <div className="mt-8 border-t border-gray-150/60 pt-6">
      <h3 className="mb-4 text-xs font-semibold text-gray-550 uppercase tracking-wider">
        Or try a sample dataset:
      </h3>
      <div className="grid gap-4 sm:grid-cols-3">
        {SAMPLE_DATASETS.map((dataset) => (
          <button
            key={dataset.filename}
            onClick={() => handleLoadSample(dataset)}
            className="group rounded-xl border border-gray-200/80 bg-white p-5 text-left shadow-sm hover:shadow-md hover:border-blue-500/80 hover:-translate-y-1 transform transition-all duration-300 cursor-pointer flex flex-col justify-between"
          >
            <div>
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl mb-3.5 border border-blue-100/30 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                {dataset.icon}
              </div>
              <h4 className="mb-1 text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                {dataset.name}
              </h4>
              <p className="text-xs text-gray-500 leading-relaxed">{dataset.description}</p>
            </div>
            <div className="mt-4 flex items-center justify-between w-full">
              <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200/30">
                {dataset.rows} points
              </span>
              <span className="text-[10px] font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center space-x-0.5">
                <span>Load</span>
                <span>→</span>
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
